import React from "react";
import { createPortal } from "react-dom";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import { CalendarDays, Truck } from "lucide-react";

export const END_OF_DAY_LOAD_REMOVAL_MODE_SOURCE_TRUCK = "source_truck";
export const END_OF_DAY_LOAD_REMOVAL_MODE_TARGET_DELIVERY_DATE = "target_delivery_date";

function normalizeText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function buildSummaryValue(value, fallback = "—") {
  return normalizeText(value) || fallback;
}

function RemovalModeOption({
  description = "",
  disabled = false,
  icon: Icon,
  isSelected = false,
  label = "",
  onSelect,
}) {
  return (
    <button
      type="button"
      aria-pressed={isSelected}
      className={[
        "grid gap-1 rounded-xl border px-3 py-2.5 text-left transition",
        isSelected
          ? "border-accent bg-accent text-white shadow-[0_0_0_1px_rgba(15,92,87,0.12)] hover:border-accent-strong hover:bg-accent-strong"
          : "border-line-soft bg-white text-text hover:border-accent/45 hover:bg-surface-muted",
        disabled ? "cursor-not-allowed opacity-60" : "",
      ].join(" ")}
      disabled={disabled}
      onClick={onSelect}
    >
      <span className="flex items-center gap-2 text-[0.88rem] font-semibold leading-5">
        {Icon ? <Icon className="h-4 w-4 shrink-0" aria-hidden="true" /> : null}
        {label}
      </span>
      {description ? (
        <span className="text-[0.75rem] font-medium leading-4.5 text-muted-strong">
          {description}
        </span>
      ) : null}
    </button>
  );
}

export default function EndOfDayLoadRemovalDialog({
  cancelLabel = "Annuler",
  groupOrderCount = 0,
  groupOrderPreview = "",
  isOpen = true,
  isPending = false,
  onCancel,
  onClose,
  onConfirm,
  sourceDate = "",
  sourceFieldLabel = "Remorque source",
  sourceTruckLabel = "",
  subtitle = "",
  targetDate = "",
  targetTruckLabel = "",
  title = "Retirer le PM ?",
}) {
  const [selectedMode, setSelectedMode] = React.useState("");
  const confirmButtonRef = React.useRef(null);
  const resolvedGroupOrderCount = Number(groupOrderCount) || 0;
  const hasGroup = resolvedGroupOrderCount > 1;

  React.useEffect(() => {
    if (isOpen) {
      setSelectedMode("");
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen || !selectedMode) {
      return undefined;
    }
    const frame = window.requestAnimationFrame(() => {
      confirmButtonRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen, selectedMode]);

  if (!isOpen) {
    return null;
  }

  const handleDismiss = () => {
    if (isPending) {
      return;
    }
    if (typeof onCancel === "function") {
      onCancel();
      return;
    }
    onClose?.();
  };

  const handleConfirm = () => {
    if (!selectedMode || isPending) {
      return;
    }
    onConfirm?.(selectedMode);
  };

  return createPortal(
    <div
      data-react-confirmation-modal="true"
      className="fixed inset-0 z-modal isolate font-sans"
      role="presentation"
    >
      <div
        className="absolute inset-0 bg-slate-950/45 px-4 py-5 backdrop-blur-xs lg:px-6"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            handleDismiss();
          }
        }}
      >
        <div className="flex min-h-full items-center justify-center">
          <section
            aria-modal="true"
            className="relative max-h-[calc(100dvh-2.5rem)] w-full max-w-[min(40rem,calc(100vw-1.5rem))] overflow-hidden rounded-overlay border border-line-soft bg-surface text-text shadow-floating"
            role="alertdialog"
          >
            <div className="h-1.5 w-full bg-accent" />
            <div className="px-4 pb-4 pt-3.5 sm:px-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <span className="inline-flex rounded-full border border-[var(--color-border-accent-muted)] bg-accent px-2.5 py-0.75 text-[0.64rem] font-semibold uppercase tracking-[0.18em] text-white">
                    Chargé en PM
                  </span>
                  <h2 className="mt-2 font-display text-[1.3rem] font-semibold leading-tight text-text">
                    {title}
                  </h2>
                  {subtitle ? (
                    <p className="mt-1 text-[0.85rem] font-semibold leading-5 text-muted-strong">
                      {subtitle}
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="shrink-0 rounded-full border border-line-soft bg-surface-muted px-3 py-1 text-[0.88rem] font-semibold text-muted-strong transition hover:border-accent hover:bg-white hover:text-accent disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={isPending}
                  onClick={handleDismiss}
                >
                  Fermer
                </button>
              </div>

              <p className="mt-3 text-[0.88rem] leading-5 text-muted-strong">
                Choisissez où placer {hasGroup ? "ce groupe PM" : "cette commande PM"} après le retrait du statut Chargé en PM.
              </p>

              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <RemovalModeOption
                  description={`Tente de remettre la commande sur ${sourceFieldLabel.startsWith("Camion") ? "le camion initial" : "la remorque initiale"}. Si la reconstruction échoue, elle ira dans Suspendues.`}
                  disabled={isPending}
                  icon={Truck}
                  isSelected={selectedMode === END_OF_DAY_LOAD_REMOVAL_MODE_SOURCE_TRUCK}
                  label={sourceFieldLabel}
                  onSelect={() => setSelectedMode(END_OF_DAY_LOAD_REMOVAL_MODE_SOURCE_TRUCK)}
                />
                <RemovalModeOption
                  description="Conserve la date cible et place la commande dans Suspendues sur cette journée."
                  disabled={isPending}
                  icon={CalendarDays}
                  isSelected={selectedMode === END_OF_DAY_LOAD_REMOVAL_MODE_TARGET_DELIVERY_DATE}
                  label="Date de livraison cible"
                  onSelect={() => setSelectedMode(END_OF_DAY_LOAD_REMOVAL_MODE_TARGET_DELIVERY_DATE)}
                />
              </div>

              <div className="mt-3 grid gap-2 rounded-xl border border-line-soft bg-surface-muted p-2.5 sm:grid-cols-2">
                <div className="rounded-xl border border-line-soft bg-white px-3 py-2">
                  <div className="text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-muted-strong">Source</div>
                  <div className="mt-1 text-[0.92rem] font-semibold leading-5 text-text">
                    {buildSummaryValue(sourceTruckLabel)}
                  </div>
                  <div className="mt-0.5 text-[0.75rem] font-medium leading-4 text-muted-strong">
                    {buildSummaryValue(sourceDate, "Date source inconnue")}
                  </div>
                </div>
                <div className="rounded-xl border border-line-soft bg-white px-3 py-2">
                  <div className="text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-muted-strong">Cible</div>
                  <div className="mt-1 text-[0.92rem] font-semibold leading-5 text-text">
                    {buildSummaryValue(targetTruckLabel)}
                  </div>
                  <div className="mt-0.5 text-[0.75rem] font-medium leading-4 text-muted-strong">
                    {buildSummaryValue(targetDate, "Date cible inconnue")}
                  </div>
                </div>
                {hasGroup ? (
                  <div className="rounded-xl border border-line-soft bg-white px-3 py-2 sm:col-span-2">
                    <div className="text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-muted-strong">Groupe PM</div>
                    <div className="mt-1 text-[0.92rem] font-semibold leading-5 text-text">
                      {resolvedGroupOrderCount} commandes{groupOrderPreview ? ` · ${groupOrderPreview}` : ""}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  className="rounded-full border border-line-soft bg-white px-4 py-2 text-[0.9rem] font-semibold text-muted-strong transition hover:border-accent hover:bg-surface-muted hover:text-text disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={isPending}
                  onClick={handleDismiss}
                >
                  {cancelLabel}
                </button>
                <button
                  ref={confirmButtonRef}
                  type="button"
                  className="rounded-full border border-accent bg-accent px-4 py-2 text-[0.9rem] font-semibold text-white transition hover:bg-accent-strong disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={!selectedMode || isPending}
                  onClick={handleConfirm}
                >
                  {isPending ? "Retrait..." : "Retirer PM"}
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>,
    getReactPortalRoot(),
  );
}
